import React, {createContext, useContext, useMemo} from 'react'
import {useGetPageByType} from '../../custom-api/hooks/use-get-page-by-type'
import RenderComponents from '../../components/editorial-components/render-components'
import Skeleton from '../editorial/skeleton'
import {Box, VisuallyHidden} from '@chakra-ui/react'
import AlternateMetaTag from '../../components/metatag/alternate'
import {getSites} from '../../utils/site-utils'
import {Helmet} from 'react-helmet'
import {BRAND_LABEL} from '../../brand'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useLocation} from 'react-router-dom'
import useMulticountryUtils from '../../custom-hooks/use-multicountry-utils'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {COUNTRY_CONTACTS, MAX_CACHE_AGE_HP, STALE_WHILE_REVALIDATE_HP} from '../../constants'

import loadable from '@loadable/component'
const SplashPage = loadable(() => import('../splash-page'))

const SeoH1Context = createContext({hasSeoH1: false})
export const useSeoH1 = () => useContext(SeoH1Context)

const Home = () => {
    const {data, isFetching, isFetched, isLoading, isSuccess, isError} =
        useGetPageByType('home_page')

    const entries = {...data}
    const sites = getSites()
    const {customParams} = getConfig()
    const location = useLocation()
    const {isSplashPage, localeIsPresentInUrl} = useMulticountryUtils()

    const {res} = useServerContext()

    const links = useMemo(() => {
        return sites
            .map((site) =>
                site.l10n.supportedLocales.map((locale) => {
                    return {
                        hreflang: locale.alias,
                        url: `/${locale.alias}`,
                    }
                }),
            )
            ?.flat()
    }, [])

    if (res) {
        if (res.locals?.ua?.isBot && !localeIsPresentInUrl()) return <SplashPage />
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE_HP}, stale-while-revalidate=${STALE_WHILE_REVALIDATE_HP}`,
        )
    }

    const seo = entries?.seo_standard || {}
    const metaTitle = seo?.meta_title
    const metaDescription = seo?.meta_description

    const currentPath = 'https://' + customParams.currentHost + location.pathname

    const currentLocale = entries?.locale
    const localeToCountry = (locale) => {
        if (!locale) return null
        const parts = locale.split('-')
        return parts.length > 1 ? parts[1].toUpperCase() : parts[0].toUpperCase()
    }
    const countryCode = localeToCountry(currentLocale)
    const contactValue = COUNTRY_CONTACTS[countryCode] || ''

    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: metaTitle,
        description: metaDescription,
        url: currentPath,
    }

    const OrganizationSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'Organization',
        name: `De'Longhi Appliances S.r.l`,
        logo: 'https://dam.delonghi.com/600x600/assets/201985',
        url: currentPath,
        contactPoint: {
            telephone: contactValue,
        },
        brand: {
            '@type': 'Brand',
            name: BRAND_LABEL,
        },
    }

    if (isSplashPage) {
        return <SplashPage />
    }

    return (
        <Box data-gtm={'homepage'} id="homepage">
            <AlternateMetaTag links={links} />

            <Helmet>
                {metaTitle && <title>{metaTitle}</title>}
                {metaDescription && <meta name="description" content={metaDescription} />}
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(OrganizationSchemaMarkup, null, 2)}
                </script>
            </Helmet>

            {seo?.h1_title && (
                <VisuallyHidden>
                    <h1 id="seoTitle">{seo.h1_title}</h1>
                </VisuallyHidden>
            )}

            {(isFetching || isLoading) &&
                !isFetched &&
                Array(entries?.editorial_components?.length || 4)
                    .fill()
                    .map((_, index) => <Skeleton key={index} />)}

            {isSuccess && !!Object.keys(entries).length ? (
                <SeoH1Context.Provider value={{hasSeoH1: !!seo?.h1_title}}>
                    <RenderComponents
                        pageComponents={entries?.editorial_components}
                        contentTypeUid="page"
                        entryUid={entries?.uid}
                        locale={entries?.locale}
                        isFetching={isFetching}
                    />
                </SeoH1Context.Provider>
            ) : (
                Array(entries?.editorial_components?.length || 4)
                    .fill()
                    .map((_, index) => <Skeleton key={index} />)
            )}

            {isError && <>{/* handle error */}</>}
        </Box>
    )
}

Home.getTemplateName = () => 'home'

export default React.memo(Home)
